import { useAuth } from '@/app/context';
import {
  AuthHeader,
  ResetPasswordForm,
  SuccessMessage,
} from '@/features/auth/components';
import { useCustomAuthenticationConfirmPassword } from '@/features/auth/hooks/use-custom-authentication-confirm-password.hook';
import { getPostAuthRedirectPath } from '@/features/auth/utils/get-post-auth-redirect-path.util';
import { LoadingSpinner } from '@/shared/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/_auth/reset')({
  component: ResetPasswordPage,
  pendingComponent: LoadingSpinner,
  validateSearch: z.object({
    email: z.string().optional(),
    token: z.string().optional(),
    /** Nome para saudação (enviado no link do e-mail de reset pelo admin) */
    name: z.string().optional(),
  }),
});

function ResetPasswordPage() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { signInWithNewPasswordRequired, getCurrentUserValues, loading } =
    useAuth();
  const { mutate: confirmResetCustomerPassword, isPending: isConfirmingReset } =
    useCustomAuthenticationConfirmPassword();
  const navigate = useNavigate();
  const search = Route.useSearch() as {
    email?: string;
    token?: string;
    name?: string;
  };
  const hasTokenFromLink = Boolean(search.email && search.token);

  const greetingName = useMemo(() => {
    const raw = search.name?.trim();
    if (!raw) return '';
    // URLSearchParams usa + para espaço; decodeURIComponent não trata +
    const withSpaces = raw.replace(/\+/g, ' ');
    try {
      return decodeURIComponent(withSpaces).trim();
    } catch {
      return withSpaces.trim();
    }
  }, [search.name]);

  const handleSubmit = async (
    password: string,
    _confirmPassword: string,
    codeOrToken: string
  ) => {
    if (search.email) {
      confirmResetCustomerPassword(
        {
          email: search.email,
          verificationCode: codeOrToken,
          newPassword: password,
        },
        {
          onSuccess: result => {
            if (result.success) {
              setSuccess(true);
              setTimeout(() => {
                navigate({ to: '/login' });
              }, 10000);
            } else {
              setError(result.error || 'Erro ao redefinir senha');
            }
          },
          onError: error => {
            let errorMessage = error.message || 'Erro ao redefinir senha';

            // Mensagens já vindas da API (detail) em português — manter
            const fromApiPt =
              errorMessage.includes('Código de verificação inválido') ||
              errorMessage.includes('inválido ou expirado');

            if (!fromApiPt) {
              if (
                errorMessage.includes('expired') ||
                errorMessage.includes('Expired') ||
                errorMessage.includes(
                  'Erro interno durante confirmação de senha'
                )
              ) {
                errorMessage =
                  'O link ou código expirou. Solicite uma nova recuperação de senha.';
              } else if (errorMessage.includes('Invalid code')) {
                errorMessage = 'Código incorreto ou expirado';
              } else if (
                errorMessage.includes('previous password') ||
                errorMessage.includes('senha anterior')
              ) {
                errorMessage =
                  'A nova senha não pode ser igual à senha anterior';
              }
            }

            setError(errorMessage);
          },
        }
      );
    } else {
      const result = await signInWithNewPasswordRequired(password);

      if (
        result &&
        typeof result === 'object' &&
        result.nextStep &&
        typeof result.nextStep === 'object' &&
        'signInStep' in result.nextStep &&
        result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
      ) {
        setSuccess(true);
        navigate({ to: '/two-factor' });
      } else if (result?.isSignedIn) {
        const userCurrentValues = await getCurrentUserValues();
        navigate({
          to: getPostAuthRedirectPath(userCurrentValues?.attributes),
        });
      } else {
        let errorMessage = result?.message || 'Erro ao confirmar a senha';

        if (
          errorMessage.includes('previous password') ||
          errorMessage.includes('senha anterior')
        ) {
          errorMessage = 'A nova senha não pode ser igual à senha anterior';
        }

        setError(errorMessage);
      }
    }
  };

  if (success) {
    return (
      <SuccessMessage
        title='Senha redefinida com sucesso!'
        message='Por segurança, lembre-se de não compartilhar sua senha.'
        buttonText='Continuar'
        buttonLink='/login'
      />
    );
  }

  return (
    <>
      <AuthHeader
        title={
          hasTokenFromLink && greetingName
            ? `Olá, ${greetingName}`
            : 'Crie sua nova senha.'
        }
        subtitle='Por favor, escolha uma senha forte e fácil de lembrar.'
      />

      <ResetPasswordForm
        showCode={!!search.email && !hasTokenFromLink}
        tokenFromUrl={hasTokenFromLink ? search.token : undefined}
        onSubmit={handleSubmit}
        loading={loading || isConfirmingReset}
        error={error}
      />
    </>
  );
}
